import React from 'react';
import { setClassNames } from '@ddc/classnames-js';
import { usePrefs } from 'wsm-common-data';
import PropTypes from 'prop-types';
import { getLoadingVehicleList, getCardTrackingData } from '../utils';
import { useConfigurableLayout } from '../hooks/useConfigurableLayout';
import VehicleLoader from './Vehicle-Loader';
import Vehicle from './Vehicle';
import DesktopCarousel from './DesktopCarousel';

const VehicleListWrapper = ({
	vehicleState,
	vehicleData,
	displayPref,
	trackEventElement,
	pageAlias,
	isNavDrawer = false
}) => {
	const {
		listItemClasses,
		listClasses: listClassesPref,
		vehicleListGapClasses,
		displayStyle
	} = usePrefs();
	const { isLoading } = vehicleState;
	const { showPriceLabel, showVin } = displayPref;

	const isSingleRow = displayStyle === 'Standard Horizontal' && !isNavDrawer;
	const {
		isConfigurableLayout,
		pageSize,
		isMultiRow,
		cardsPerRow,
		displayedVehicles,
		showCarousel
	} = useConfigurableLayout({ isSingleRow, isLoading, vehicleData });

	let maxVehiclesPerRow;
	let numberOfRows;
	if (isNavDrawer) {
		maxVehiclesPerRow = 2;
		numberOfRows = 3;
	} else if (isSingleRow) {
		maxVehiclesPerRow = 4;
		numberOfRows = 1;
	} else {
		maxVehiclesPerRow = 3;
		numberOfRows = 2;
	}
	// Use pageSize so skeleton count matches the configured layout.
	const displayLimit = isConfigurableLayout
		? pageSize
		: maxVehiclesPerRow * numberOfRows;
	// Multi-row keeps a short last row aligned under the full rows above it.
	const leftJustifyVehicles = isMultiRow || displayedVehicles.length < 3;

	// Use custom gap class or default 2% gap
	const isCustomVehicleListGap = vehicleListGapClasses !== '';
	const commonVehicleClasses = [
		listItemClasses !== 'BLANK' ? listItemClasses : '',
		'vehicle-card',
		'overflow-hidden',
		'position-relative',
		'my-1',
		'mb-4',
		'bg-contrast-low'
	];
	let vehicles;
	const gap = 2;
	// Card basis = 100/columns, minus the 2% gap so the row still fits: 2-col →
	// 48%, 3-col → 31%, 4-col → 23%. Custom pixel gaps skip the subtraction.
	const cardWidthPercent = isCustomVehicleListGap
		? Math.floor(100 / maxVehiclesPerRow)
		: Math.floor(100 / maxVehiclesPerRow) - gap;
	const cardStyle = !isSingleRow ? { flex: `0 0 ${cardWidthPercent}%` } : {};

	if (isLoading) {
		// Creates a list of skeletons
		vehicles = getLoadingVehicleList(displayLimit).map((vehicle) => {
			const { id } = vehicle;

			let prefClasses;

			if (showVin && showPriceLabel) {
				prefClasses = 'vehicle-card-skeleton-label-vin-pref';
			} else if (showVin) {
				prefClasses = 'vehicle-card-skeleton-vin-pref';
			} else if (showPriceLabel) {
				prefClasses = 'vehicle-card-skeleton-label-pref';
			} else {
				prefClasses = 'vehicle-card-skeleton-non-pref';
			}

			const skeletonClasses = [
				!isSingleRow ? 'vehicle-card-skeleton' : prefClasses,
				'mt-1 mb-3',
				...commonVehicleClasses
			];

			return (
				<li
					className={setClassNames(skeletonClasses)}
					style={cardStyle}
					key={`loader_${id}`}
					data-testid="card-li-loader"
				>
					<VehicleLoader
						vehicleState={vehicleState}
						displayStyle={displayStyle}
					/>
				</li>
			);
		});
	} else {
		// Creates a list vehicle cards
		vehicles = displayedVehicles.map((vehicle, index) => (
			<li
				className={setClassNames(commonVehicleClasses)}
				key={vehicle.uuid}
				style={cardStyle}
				data-testid="card-li"
			>
				<Vehicle
					info={vehicle}
					index={index}
					trackEventElement={trackEventElement}
					trackingData={getCardTrackingData(vehicle)}
					pageAlias={pageAlias}
				/>
			</li>
		));
	}

	let layoutClasses;
	if (isMultiRow) {
		// A grid (no static-width/d-flex) so the static-width nth-child rules
		// don't hide every card past the first row.
		layoutClasses = 'multi-row';
	} else if (!isSingleRow) {
		layoutClasses = 'hide-from-7';
	} else {
		layoutClasses = 'static-width overflow-hidden';
	}
	const listClasses = [
		listClassesPref !== 'BLANK' ? listClassesPref : '',
		layoutClasses,
		leftJustifyVehicles
			? 'justify-content-left'
			: 'justify-content-between',
		'list-unstyled',
		'vehicle-list',
		isMultiRow ? '' : 'd-flex',
		isMultiRow ? '' : 'flex-wrap',
		'mb-0',
		vehicleListGapClasses
	];

	// Use inline gap style only if custom gap class is not set
	const cardContainer = !isCustomVehicleListGap ? { gap: `${gap}%` } : {};

	const vehicleList = (
		<ul
			className={setClassNames(listClasses)}
			style={cardContainer}
			data-testid="static-width-carousel"
		>
			{vehicles}
		</ul>
	);

	if (showCarousel) {
		// The single-row list doubles as the placeholder while the carousel
		// chunk loads, so nothing shifts in the meantime.
		return (
			<DesktopCarousel
				vehicleData={displayedVehicles}
				slidesToShow={cardsPerRow}
				trackEventElement={trackEventElement}
				pageAlias={pageAlias}
				placeholder={vehicleList}
			/>
		);
	}

	return vehicleList;
};

VehicleListWrapper.propTypes = {
	vehicleState: PropTypes.shape({
		isLoading: PropTypes.bool.isRequired,
		fadeIn: PropTypes.bool.isRequired
	}),
	vehicleData: PropTypes.arrayOf(
		PropTypes.shape({
			uuid: PropTypes.string.isRequired,
			imagePath: PropTypes.string.isRequired,
			link: PropTypes.string,
			totalPrice: PropTypes.oneOfType([
				PropTypes.string.isRequired,
				PropTypes.number.isRequired
			]),
			bodyStyle: PropTypes.string,
			cityFuelEconomy: PropTypes.string,
			highwayFuelEconomy: PropTypes.string,
			make: PropTypes.string,
			model: PropTypes.string,
			odometer: PropTypes.string,
			stockNumber: PropTypes.string,
			vin: PropTypes.string,
			year: PropTypes.number
		})
	).isRequired,
	displayPref: PropTypes.shape({
		showPriceLabel: PropTypes.bool.isRequired,
		showVin: PropTypes.bool.isRequired
	}),
	trackEventElement: PropTypes.string.isRequired,
	pageAlias: PropTypes.string.isRequired,
	isNavDrawer: PropTypes.bool
};

export default VehicleListWrapper;
